import type { UppKonvaLabelFontSize } from '../../model/upp-konva-types';
import { floorPlanFiniteNumber, floorPlanFinitePositive } from '../geometry/floor-plan-konva-sync';

/**
 * Presentation preferences for the floor plan (no low-level graphics engine terms in the public API).
 */
export interface UppFloorPlanPreferences {
  /** Padding between the host edge and the usable rectangle (CSS pixels). */
  marginPx: number;
  /**
   * When true, picks 0° vs 90° orientation to maximize fit scale.
   * Default false so the host controls orientation via {@link viewOrientationDeg}.
   */
  autoOptimizeOrientation: boolean;
  /**
   * Screen rotation of the room content group when {@link autoOptimizeOrientation} is false.
   * Only **0** or **90** (degrees, Konva-positive = counter-clockwise) are supported; other values are treated as **0**.
   */
  viewOrientationDeg: 0 | 90;
  /** Upper bound for `devicePixelRatio` on the layer canvases (sharpness vs performance). */
  pixelRatioMax: number;
  /** Multiplier ≥ 0.25 applied to the fit scale after computing layout. */
  viewZoom: number;
  /** Scales {@link UppFloorPlanTheme.labelFontSize} for element label badges (primary + sublabel). */
  labelSize: UppKonvaLabelFontSize;
}

export const UPP_FLOOR_PLAN_PREFERENCES_DEFAULT: UppFloorPlanPreferences = {
  marginPx: 12,
  autoOptimizeOrientation: false,
  viewOrientationDeg: 0,
  pixelRatioMax: 2,
  viewZoom: 1,
  labelSize: 'NORMAL',
};

/**
 * English comment normalized.
 * English comment normalized.
 */
export function parseUppKonvaLabelFontSize(raw: string | undefined | null): UppKonvaLabelFontSize {
  if (raw === undefined || raw === null) {
    return 'NORMAL';
  }
  const u = String(raw).trim().toUpperCase();
  if (u === 'SMALL') {
    return 'SMALL';
  }
  if (u === 'LARGE') {
    return 'LARGE';
  }
  if (u === 'NORMAL') {
    return 'NORMAL';
  }
  return 'NORMAL';
}

export function mergeUppFloorPlanPreferences(
  partial: Partial<UppFloorPlanPreferences> | null | undefined,
): UppFloorPlanPreferences {
  const m = { ...UPP_FLOOR_PLAN_PREFERENCES_DEFAULT, ...partial };
  const marginPx = Math.max(
    0,
    floorPlanFiniteNumber(m.marginPx, UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.marginPx),
  );
  const viewZoom = floorPlanFinitePositive(m.viewZoom, UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.viewZoom);
  const pixelRatioMax = floorPlanFinitePositive(
    m.pixelRatioMax,
    UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.pixelRatioMax,
  );
  return {
    ...m,
    marginPx,
    viewZoom,
    pixelRatioMax,
    viewOrientationDeg: m.viewOrientationDeg === 90 ? 90 : 0,
    labelSize: parseUppKonvaLabelFontSize(m.labelSize as unknown as string),
  };
}
